import { useSearchParams } from "react-router-dom";

/**
 * Filter and search state, backed **only** by a URL search param.
 *
 * No localStorage: the URL is the single source of truth, so a filtered view is
 * shareable, bookmarkable, and survives back/forward and reload. Clearing a filter
 * removes the param entirely, so a clean view has a clean URL.
 *
 * `key` is namespaced per screen (`"clients:q"`) because two lists can appear on one
 * route — tabs, embedded lists — and would otherwise fight over the same param.
 *
 * Ported from bd-admin, including the detail that makes it correct: see the comment
 * inside the setter. `.claude/docs/ui-patterns.md` forbids "simplifying" it.
 */
export function useFilterParam(key: string, defaultValue = ""): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(key) ?? defaultValue;

  const setValue = (next: string): void => {
    // Read the URL live rather than react-router's render-time `searchParams`
    // snapshot. Each hook instance closes over its own snapshot, so a "clear all
    // filters" handler that fires several setters synchronously would have every call
    // but the last silently overwritten. `history.replaceState` — which `navigate`
    // uses underneath — mutates `window.location` synchronously, so reading it here
    // always reflects the previous call even before React re-renders.
    const params = new URLSearchParams(window.location.search);
    if (next && next !== defaultValue) params.set(key, next);
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  return [value, setValue];
}

/**
 * Multi-value variant: the param holds a comma-joined list (`?owner=id1,id2`), and an
 * empty selection removes it.
 *
 * The returned array is a fresh value every render — compare by `.join(",")`, never by
 * reference, in effect dependencies.
 */
export function useFilterParamList(key: string): [string[], (value: string[]) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = raw ? raw.split(",").filter(Boolean) : [];

  const setValue = (next: string[]): void => {
    // See useFilterParam — read the live URL, not the closure snapshot.
    const params = new URLSearchParams(window.location.search);
    if (next.length) params.set(key, next.join(","));
    else params.delete(key);
    setSearchParams(params, { replace: true });
  };

  return [value, setValue];
}

/** Boolean variant. The param is present only when the value differs from the default. */
export function usePersistedBool(key: string, defaultValue: boolean): [boolean, (value: boolean) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = raw === null ? defaultValue : raw === "true";

  const setValue = (next: boolean): void => {
    // See useFilterParam — read the live URL, not the closure snapshot.
    const params = new URLSearchParams(window.location.search);
    if (next === defaultValue) params.delete(key);
    else params.set(key, String(next));
    setSearchParams(params, { replace: true });
  };

  return [value, setValue];
}
